// AdminPanel — User Management page (admin-only route).
// Extracted VERBATIM from src/App.jsx (C1 monolith split, 2026-05-29). Behavior-preserving:
// the function body is byte-identical to its prior in-App.jsx form; only the surrounding
// imports were added so the module resolves its dependencies directly (no import back to
// App.jsx — avoids a require cycle). Internal-only: 0 external importers, not re-exported.
import React, { useState, useEffect, useRef } from "react";
import * as tz from '../lib/tz.js';   // DST-aware business time (Europe/Kyiv)
import { ChevronLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { isAdmin, todayGMT2, genId, ALL_PAGES, hashPassword, getPersonColor } from '../utils.js';
import { stampNewRecord, stampUpdate } from '../lib/record-stamp.js';
import { inp } from '../styles/inline-styles.js';
import { telegramTest, telegramNotify } from '../lib/telegram-admin.js';
import { VERSION } from '../version.js';
import { INITIAL_USERS } from '../lib/initial-users.js';
import { I } from '../icons.jsx';
import { useToast } from '../contexts/toast-context.jsx';
import { API_BASE } from '../lib/endpoints.js';
import { getSessionToken, resetSessionTokenCache } from '../utils/session.js';
import { lsSave } from '../lib/local-storage.js';
import { SyncStatus } from './SyncStatus.jsx';
import { ActivityLogsViewer } from './ActivityLogsViewer.jsx';
import { ServerDiagnostics } from './ServerDiagnostics.jsx';
import { RestoreDatabase } from './RestoreDatabase.jsx';
import { UnbanIPWidget } from './UnbanIPWidget.jsx';
import { PageAccessToggles } from './PageAccessToggles.jsx';
import { Modal } from './Modal.jsx';
import { ConfirmDialog } from './ConfirmDialog.jsx';
import { Field } from './Field.jsx';

function AdminPanel({ users, setUsers, wallets, setWallets, onBack, user, onNavSecurity, onNavBotMessages }) {
  const { addToast } = useToast(); // FIX: was missing — toast calls were going to noop

  // Подтверждения. Промис-обёртка вокруг ConfirmDialog — тот же приём, что в
  // SecurityPanel (30fa2d5558): системный confirm() был синхронным и читался как
  // `if (!confirm(...)) return;`, askConfirm сохраняет эту форму, поэтому тела
  // действий не пришлось разбирать на «спросить» и «сделать». Системные диалоги
  // рисует ОС — они игнорируют тему приложения и блокируют вкладку.
  const [confirmState, setConfirmState] = useState(null);
  const askConfirm = (opts) => new Promise((resolve) => setConfirmState({ opts, resolve }));
  const closeConfirm = (answer) => {
    confirmState?.resolve(answer);
    setConfirmState(null);
  };
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false); // FIX: show Saving... on button
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", pageAccess: ["overview","settings"] });
  const [delConfirm, setDelConfirm] = useState(null);
  const isSavingUser = useRef(false);
    useEffect(() => {
    if (!delConfirm) return;
    const handler = (e) => { if (e.key === 'Escape') setDelConfirm(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [delConfirm]);
  const [editingWallet, setEditingWallet] = useState(null); // null or wallet id being edited
  const [walletForm, setWalletForm] = useState({ date: "", trc: "", erc: "", usdc: "", btc: "" }); // FIX: added usdc (was missing — silently overwrote usdc on save)

  // `wallets` приходит пропом и обновляется поллингом (App.jsx, каждые 15 с). Раньше
  // между вопросом и отправкой не было времени: системный confirm() блокировал поток
  // целиком. Наш диалог ждёт человека сколько угодно, а сохранение кошельков — это POST
  // ВСЕГО массива. Значит список на отправку берём ПОСЛЕ ответа, из рефа с актуальным
  // значением: иначе всё, что приехало за время открытого диалога, стирается с сервера.
  const walletsRef = useRef(wallets);
  useEffect(() => { walletsRef.current = wallets; }, [wallets]);

  // ── Direct API save for all wallet mutations ──
  // NEVER use debounced auto-save — updatedAt not bumped on edit causes server to reject updates
  const saveWalletsDirectly = async (newWallets, actionLabel, deletedId = null) => {
    try {
      const token = getSessionToken();
      const body = { data: newWallets, user: user?.email };
      if (deletedId) body.deleted = [deletedId];
      const res = await fetch(`${API_BASE}/wallets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) {
        setWallets(newWallets);
        lsSave('wallets', newWallets);
        addToast('✅ Wallet saved', 'success');
        return true;
      } else {
        addToast(`❌ Wallet save failed: ${d.error || res.statusText}`, 'error');
        return false;
      }
    } catch (e) {
      addToast(`❌ Network error: ${e.message}`, 'error');
      return false;
    }
  };

  // v10.15: Cache last valid users list — fall back to INITIAL_USERS instead of empty "Loading..."
  const usersCache = useRef(users && users.length > 0 ? users : INITIAL_USERS);
  if (users && users.length > 0) usersCache.current = users;
  const displayUsers = usersCache.current;

  // ── Direct API save for all user mutations ──
  // NEVER use debounced auto-save for user management — the updatedAt guard can block saves.
  // Every add/edit/delete must go directly to the server immediately, like payment status changes.
  const saveUsersDirectly = async (newUsers, actionLabel) => {
    try {
      resetSessionTokenCache(); // v28.09 FIX: always force fresh token from localStorage (avoids "wrong session" after server restart)
      const token = getSessionToken();
      // ────────────────────────────────────────────────────────────────────
      // v31.62 (2026-04-29): STRIP telegramChatId / telegramUsername из ВСЕХ users
      // перед отправкой mass save. Backend's POST /api/users отвечает за structural
      // data (email, name, passwordHash, pageAccess), НЕ за telegram fields.
      // Telegram fields owned ИСКЛЮЧИТЕЛЬНО через PUT /api/users/:email — single
      // source of truth, no stale-state leakage.
      //
      // Root cause "ghost preserve alerts" (audit log 20-23 Apr 2026):
      // 1. Admin opens UI → setUsers(localStorage.users) instant paint (potentially stale).
      // 2. Admin edits user X (e.g. password) → handleUpdateUser builds newUsers FROM
      //    current `users` state (still stale).
      // 3. saveUsersDirectly POSTs all users — for unmodified users, stale telegramChatId='' leaks.
      // 4. Backend's preserveTelegramField saves data + fires audit + Telegram alert.
      // Result: boss receives "PRESERVED" alerts даже без admin actually clearing fields.
      //
      // Fix: strip telegram fields entirely. Backend `'telegramChatId' in sanitizedU`
      // check (server.cjs:1746) returns false → preserveTelegramField never called →
      // no audit, no alert. Existing telegramChatId preserved natural way through
      // `{...e, ...sanitizedU}` spread merge (sanitizedU не имеет key → spread не overrides).
      //
      // Defense-in-depth: backend's preserveTelegramField helper UNCHANGED — safety net
      // если ever какой-то path забыл strip.
      // ────────────────────────────────────────────────────────────────────
      const stripped = newUsers.map(u => {
        const { telegramChatId, telegramUsername, ...rest } = u;
        return rest;
      });
      const res = await fetch(`${API_BASE}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ data: stripped, user: user?.email }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) {
        setUsers(newUsers);
        // FIX: removed lsSave('users', newUsers) — newUsers has no updated pageAccess (server ignores it in POST /users)
        // WS broadcast immediately pushes authoritative user list back; lsSave here would write stale pageAccess
        return true;
      } else {
        addToast(`❌ Failed to save: ${d.error || res.statusText}`, 'error');
        return false;
      }
    } catch (e) {
      addToast(`❌ Network error: ${e.message}`, 'error');
      return false;
    }
  };

  // ── Direct API save for per-user color (user_colors фича, 2026-07-13) ──
  // PUT /api/users/:email — точечная правка ОДНОГО поля у ОДНОГО юзера, НЕ через
  // bulk saveUsersDirectly. Зеркалит существующий паттерн telegram-полей (Step 2
  // в handleUpdateUser ниже, комментарий "root cause ghost preserve alerts" —
  // тот же класс риска, что бриф §2.4.2 называет "ГЛАВНЫЙ риск фичи" для color):
  // если бы цвет ехал через bulk save, stale-копия users[] в другой открытой
  // вкладке могла бы затереть чужую только что сохранённую правку. Single-field
  // PUT структурно исключает это — save всегда касается ровно одной записи.
  // color=null означает явный сброс (кнопка Reset) — backend удаляет поле.
  const saveUserColorDirectly = async (email, color) => {
    try {
      const tok = localStorage.getItem('blitz_token') || getSessionToken();
      const res = await fetch(`/api/users/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tok}` },
        body: JSON.stringify({ color }),
      });
      if (res.ok) {
        addToast(color ? '✅ Color updated' : '✅ Color reset to default', 'success');
        // Локально ничего не трогаем — authoritative users[] придёт через WS-broadcast
        // (тот же паттерн, что у permissions/telegram save выше).
      } else {
        const err = await res.json().catch(() => ({}));
        addToast(`❌ Color save failed: ${err.error || res.statusText}`, 'error');
      }
    } catch (e) {
      addToast(`❌ Network error: ${e.message}`, 'error');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name.trim()) { alert('Name is required'); return; }
    // FIX: email format validation (was missing — allowed 'test' or '@' as email)
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newUser.email || !emailRx.test(newUser.email.trim())) { alert('Please enter a valid email address'); return; }
    // FIX: minimum password length (was missing — allowed single-char passwords)
    if (!newUser.password || newUser.password.length < 6) { alert('Password must be at least 6 characters'); return; }
    // FIX: check duplicate email case-insensitively
    if (users.some(u => u.email.toLowerCase() === newUser.email.toLowerCase().trim())) { alert('A user with this email already exists'); return; }
    const hashed = hashPassword(newUser.password);
    const newEntry = { email: newUser.email, passwordHash: hashed, name: newUser.name, pageAccess: newUser.pageAccess || ["overview","settings"], ...stampNewRecord() };
    const newUsers = [...users, newEntry];
    const ok = await saveUsersDirectly(newUsers, `add ${newUser.email}`);
    if (ok) {
      addToast(`✅ User ${newUser.name} added successfully`, 'success');
      setNewUser({ email: "", password: "", name: "", pageAccess: ["overview","settings"] });
      setAddOpen(false);
    }
  };

  // v31.42 Fix #3: Admin reset password handler — вызывает backend endpoint который
  // генерит random password + обновляет hash + invalidates sessions. Response содержит
  // plain password который admin копирует и передаёт user'у в TG. После login user меняет
  // через Settings на свой пароль.
  const [resetPasswordModal, setResetPasswordModal] = useState(null); // { email, password } или null
  const [resettingPassword, setResettingPassword] = useState(false);
  const handleResetPassword = async () => {
    if (!editUser?.email) return;
    if (!(await askConfirm({
      title: `Сбросить пароль для ${editUser.email}?`,
      message: 'Все активные сессии пользователя будут завершены. Новый пароль будет показан (один раз) для передачи в TG.',
      confirmLabel: 'Сбросить пароль',
      danger: true,
    }))) return;
    setResettingPassword(true);
    try {
      const tok = localStorage.getItem('blitz_token') || getSessionToken();
      const res = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(editUser.email)}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tok}` }
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setResetPasswordModal({ email: data.email, password: data.password });
        addToast(`Password reset for ${data.email}`, 'success');
      } else {
        addToast(`Reset failed: ${data.error || res.status}`, 'error');
      }
    } catch (e) {
      addToast(`Reset failed: ${e.message}`, 'error');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    if (isSavingUser.current) return;
    isSavingUser.current = true;
    setSavingUser(true); // show "Saving..." on button
    try {
    // Step 1: Save name/email/password via normal save (server ignores pageAccess from this)
    // FIX: check email collision — was missing, allowed overwriting another user's account
    const emailLower = (editUser.email || '').trim().toLowerCase();
    const collision = users.find(u => u.email.toLowerCase() === emailLower && u.email !== editUser.originalEmail);
    if (collision) { addToast(`❌ Email already used by ${collision.name}`, 'error'); return; }
    const newUsers = users.map(u => {
      if (u.email !== editUser.originalEmail) return u;
      // v31.62: telegram fields НЕ включаем в Step 1 mass save body — saveUsersDirectly
      // делает strip всё equally, но cleaner если update object изначально без telegram.
      // Telegram fields handled через Step 2 PUT /api/users/:email с diff'ed values only.
      const updated = { ...u, email: editUser.email.trim(), name: editUser.name, updatedAt: stampUpdate(u.updatedAt), knownIps: u.knownIps || [] };
      if (editUser.password && editUser.password.trim()) updated.passwordHash = hashPassword(editUser.password);
      return updated;
    });
    const ok = await saveUsersDirectly(newUsers, `edit ${editUser.email}`);
    if (!ok) return;
    // Step 2: Always save permissions — server is authoritative, endpoint is idempotent
    // FIX: was checking paChanged via JSON.stringify comparison (unreliable if array order differs)
    // Save telegram fields + name via PUT /api/users/:email
    //
    // Frontend fix 2026-04-17: отправляем в body ТОЛЬКО те telegram поля,
    // которые реально изменились относительно оригинала. Раньше всегда
    // отправляли `telegramChatId: editUser.telegramChatId || ''` — это давало
    // пустую строку при undefined и backend (preserveTelegramField) шлёт
    // PRESERVED alert боссу при каждом admin save.
    //
    // Корень HIGH #1 bug'а был именно здесь. Backend-защита остаётся как
    // safety net, но теперь не триггерится зря при admin save без изменения
    // telegram полей.
    //
    // Если админ намеренно очистил поле (orig непусто → новое пусто) —
    // ВСЁ ЕЩЁ шлём '' → backend preserveTelegramField защищает (by design,
    // clear через UI запрещён — только прямая правка users.json на сервере).
    try {
      const _putTok=localStorage.getItem('blitz_token')||getSessionToken();
      const _origUser = users.find(u => u.email === editUser.originalEmail) || {};
      const _newTcId = String(editUser.telegramChatId || '').trim();
      const _origTcId = String(_origUser.telegramChatId || '').trim();
      const _newTcUser = String(editUser.telegramUsername || '').trim();
      const _origTcUser = String(_origUser.telegramUsername || '').trim();
      const _putBody = { name: editUser.name || '' };
      // Включить telegram поля в body ТОЛЬКО если админ их реально изменил
      if (_newTcId !== _origTcId) _putBody.telegramChatId = _newTcId;
      if (_newTcUser !== _origTcUser) _putBody.telegramUsername = _newTcUser;
      const _putRes=await fetch(`/api/users/${encodeURIComponent(editUser.email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${_putTok}` },
        body: JSON.stringify(_putBody),
      });
      if (!_putRes.ok) { addToast('⚠️ Could not save Telegram fields', 'error'); }
    } catch(e) { addToast('⚠️ Could not save Telegram fields: '+e.message, 'error'); }
    if (editUser.pageAccess && Array.isArray(editUser.pageAccess)) {
      try {
        const r = await fetch(`/api/users/${encodeURIComponent(editUser.email)}/permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('blitz_token') || getSessionToken()}` }, // v28.09 FIX: fresh token
          body: JSON.stringify({ pageAccess: editUser.pageAccess }),
        });
        if (!r.ok) { addToast('⚠️ Permissions save failed', 'error'); return; }
        addToast('✅ User saved successfully', 'success');
      } catch(e) { addToast('❌ Permissions error: ' + e.message, 'error'); return; }
    }
    setEditUser(null);
    } finally {
      isSavingUser.current = false;
      setSavingUser(false);
      // 2s cooldown before allowing another save (prevents double-tap re-save)
      setTimeout(() => { isSavingUser.current = false; }, 2000);
    }
  };

  const handleDeleteUser = async (email) => {
    if (isAdmin(email)) return;
    if (!(await askConfirm({
      title: 'Удалить пользователя?',
      message: `${email}\n\nОн больше не сможет войти в систему.`,
      confirmLabel: 'Удалить',
      danger: true,
    }))) return;
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getSessionToken()}` },
        body: JSON.stringify({ user: user?.email }),
      });
      if (res.ok) {
        // Тот же класс, что у кошельков: между вопросом и ответом сервера список мог
        // обновиться поллингом. Здесь удаление идёт отдельным DELETE (сервер не
        // перезаписывается целиком), но локальный список всё равно берём свежий.
        setUsers(prev => (prev || []).filter(u => u.email !== email));
        addToast(`✅ User removed`, 'success');
        // FIX: removed lsSave('users') — would write stale pageAccess to localStorage; WS broadcast handles sync
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`❌ Failed to delete user: ${err.error || res.statusText}`);
        // DO NOT update local state if server delete failed — poll will keep showing correct server data
      }
    } catch (e) {
      alert(`❌ Network error deleting user: ${e.message}. Please try again.`);
      // DO NOT update local state — let server remain source of truth
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#FFFFFF", fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif", color: "#0F172A" }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />

      <header className="blitz-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #E2E8F0", background: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 100, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {I.logo}
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 16, letterSpacing: -0.3 }}>Blitz CRM</span>
          <span style={{ fontSize: 10, color: "#64748B", fontFamily: "'JetBrains Mono',monospace", background: "#E2E8F0", padding: "2px 7px", borderRadius: 6 }}>v{VERSION}</span>
          <span style={{ color: "#64748B", fontSize: 13 }}>/ Admin</span>
          <SyncStatus />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onNavSecurity && (
            <button onClick={onNavSecurity} style={{ display: "flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#8B5CF6,#A78BFA)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 8, boxShadow: "0 4px 12px rgba(139,92,246,0.3)" }}
              title="Security Dashboard — audit, sessions, IP management">
              🛡️ <span>Security</span>
            </button>
          )}
          {onNavBotMessages && (
            <button onClick={onNavBotMessages} style={{ display: "flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#0EA877,#2DD4A7)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "7px 12px", borderRadius: 8, boxShadow: "0 4px 12px rgba(14,168,119,0.3)" }}
              title="Bot Messages — track / edit / delete every message the bot sends">
              🤖 <span>Bot Messages</span>
            </button>
          )}
          <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #E2E8F0", color: "#64748B", cursor: "pointer", fontSize: 12, fontWeight: 500, padding: "7px 12px", borderRadius: 8 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#38BDF8"; e.currentTarget.style.color = "#38BDF8"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#334155"; e.currentTarget.style.color = "#94A3B8"; }}
          ><ChevronLeft size={14} strokeWidth={2.5} /><span>Back to Dashboard</span></button>
        </div>
      </header>

      <main style={{ maxWidth: 1760, margin: "0 auto", padding: "20px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>User Management</h1>
          <button onClick={() => setAddOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "linear-gradient(135deg,#0EA5E9,#38BDF8)", border: "none", borderRadius: 10, color: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(14,165,233,0.3)" }}
          >{<Plus size={16} strokeWidth={2} />} Add User</button>
        </div>

        {/* Users Table */}
        <div className="blitz-user-table-wrap" style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "auto" }}>
          {(!displayUsers || displayUsers.length === 0) ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>👥</div>
              Loading users... If this persists, check server connection.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>Name</th>
                  {/* user_colors фича (2026-07-13) — §2.5.1 брифа: swatch/пикер прямо в строке юзера */}
                  <th style={{ padding: "10px 16px", textAlign: "center", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>Color</th>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>Email</th>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>Last Login</th>
                  <th style={{ padding: "10px 16px", textAlign: "center", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>Access</th>
                  <th style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayUsers.map((u, i) => (
                  <tr key={u.email} style={{ borderBottom: i < users.length - 1 ? "1px solid #F1F5F9" : "none" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(56,189,248,0.03)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={{ padding: "10px 16px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{u.name}</span>
                        {isAdmin(u.email) && (
                          <span style={{ padding: "1px 6px", borderRadius: 6, background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.3)", fontSize: 9, fontWeight: 700, color: "#F87171", textTransform: "uppercase" }}>Admin</span>
                        )}
                        {u.telegramChatId && <span title="2FA enabled" style={{ padding:"1px 6px", borderRadius:8, background:"#0f3d2e", color:"#34d399", fontSize:9, fontWeight:700 }}>2FA</span>}
                        {u.telegramUsername && <span title={`Bot whitelist: @${u.telegramUsername}`} style={{ padding:"1px 6px", borderRadius:8, background:"#0f2d3d", color:"#38bdf8", fontSize:9, fontWeight:700 }}>@{u.telegramUsername}</span>}
                      </div>
                    </td>
                    <td style={{ padding: "10px 16px", textAlign: "center" }}>
                      {/* user_colors фича (2026-07-13) — native <input type=color> = полный RGB,
                          без новых зависимостей. Value = эффективный цвет (§2.5.3: свой ИЛИ
                          легаси/hash-дефолт), чтобы пикер никогда не открывался на чёрном.
                          Uncontrolled (defaultValue, НЕ value) + key на текущем значении —
                          перемонтирует input, когда u.color меняется извне (WS от другого
                          клиента/self-service), держа swatch в синхроне без React state per-row. */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        <input
                          type="color"
                          key={`color-${u.email}-${u.color || getPersonColor(u.name)}`}
                          defaultValue={u.color || getPersonColor(u.name)}
                          // Слушаем нативный `change` (не React onChange, который для
                          // type=color фактически маппится на native `input` — в Chrome
                          // стреляет multiple раз при перетаскивании внутри picker'а).
                          // Property-assignment в ref callback = единственный listener,
                          // безопасно per-row внутри .map() без hooks (Rules of Hooks).
                          ref={el => { if (el) el.onchange = (e) => saveUserColorDirectly(u.email, e.target.value); }}
                          title="Custom color"
                          style={{ width: 26, height: 26, padding: 0, border: "1px solid #E2E8F0", borderRadius: 6, cursor: "pointer", background: "none" }}
                        />
                        {u.color && (
                          <button onClick={() => saveUserColorDirectly(u.email, null)} title="Reset to default color"
                            style={{ background: "transparent", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: 12, padding: 2, lineHeight: 1 }}
                          >✕</button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "10px 16px", fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#64748B" }}>{u.email}</td>
                    <td style={{ padding: "10px 16px", fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: u.lastLogin ? "#64748B" : "#CBD5E1", whiteSpace: "nowrap" }}>
                      {u.lastLogin ? tz.fmtDateTime(new Date(u.lastLogin)) : "Never"}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
                        {ALL_PAGES.map(pg => {
                          const hasAccess = isAdmin(u.email) || (Array.isArray(u.pageAccess) ? u.pageAccess : []).includes(pg.key);
                          return hasAccess ? <span key={pg.key} style={{ padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 600, background: `${pg.color}15`, color: pg.color }}>{pg.label}</span> : null;
                        })}
                      </div>
                    </td>
                    <td style={{ padding: "10px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button onClick={async () => {
                          // v31.62 followup: fetch FULL user record (с telegram fields)
                          // через admin endpoint перед открытием dialog. Regular GET /api/users
                          // strips telegram (security), поэтому admin не видел current values
                          // в edit dialog → risk: silent override existing chatId. Now: dialog
                          // показывает actual server-side values (или empty если не set).
                          let full = u;
                          try {
                            const tok = localStorage.getItem('blitz_token') || getSessionToken();
                            const res = await fetch(`/api/admin/users/${encodeURIComponent(u.email)}`, {
                              headers: { 'Authorization': `Bearer ${tok}` }
                            });
                            if (res.ok) full = await res.json();
                          } catch { /* fallback к u from local state */ }
                          setEditUser({
                            ...full,
                            originalEmail: u.email,
                            telegramChatId: full.telegramChatId || '',
                            telegramUsername: full.telegramUsername || '',
                          });
                        }} title="Edit"
                          style={{ background: "rgba(14,165,233,0.08)", border: "1px solid rgba(14,165,233,0.2)", borderRadius: 6, padding: "5px 10px", cursor: "pointer", color: "#38BDF8", fontSize: 12, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}
                        >{<Pencil size={14} strokeWidth={2} />}</button>
                        {!isAdmin(u.email) && (
                          <button onClick={() => handleDeleteUser(u.email)} title="Remove"
                            style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: 6, padding: "5px 10px", cursor: "pointer", color: "#F87171", fontSize: 12, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}
                          >{<Trash2 size={14} strokeWidth={2} />}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Telegram Bot Section */}
        <div style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>📬 Telegram Notifications</h2>
          <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 14, padding: "24px" }}>
            <p style={{ color: "#64748B", fontSize: 14, marginBottom: 16 }}>
              Notifications are sent to your Telegram group when payments are created, status changes, etc.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button onClick={async () => {
                const res = await telegramTest();
                alert(res.status === "ok" ? "✅ Telegram bot is connected!" : "❌ Telegram not configured: " + (res.error || "Unknown error"));
              }}
                style={{ padding: "10px 20px", background: "linear-gradient(135deg, #0088cc, #00aaff)", border: "none", borderRadius: 8, color: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}
              >🤖 Test Connection</button>
              <button onClick={async () => {
                const res = await telegramNotify("🔔 Test notification from Blitz CRM — everything is working!");
                alert(res.ok ? "✅ Test message sent!" : "❌ Failed: " + (res.error || "Unknown error"));
              }}
                style={{ padding: "10px 20px", background: "transparent", border: "2px solid #0088cc", borderRadius: 8, color: "#0088cc", cursor: "pointer", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}
              >📤 Send Test Message</button>
            </div>
          </div>
        </div>

        {/* Security — IP Unban */}
        <div style={{ marginTop: 32, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 14, padding: "20px 24px" }}>
          <h2 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: "#C2410C" }}>🛡️ Security — Unban IP</h2>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#92400E" }}>If a device gets blocked (shows "Forbidden" during balance check), enter its IP here to unban it.</p>
          <UnbanIPWidget />
        </div>

        {/* Activity Logs Viewer */}
        <ActivityLogsViewer />

        {/* Wallets Section */}
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>💳 Wallets</h2>
            <button onClick={async () => {
              const newW = { id: genId(), date: todayGMT2(), trc: "", erc: "", btc: "", ...stampNewRecord() };
              const ok = await saveWalletsDirectly([newW, ...(wallets||[])], 'add wallet');
              if (ok) { setEditingWallet(newW.id); setWalletForm({ date: newW.date, trc: "", erc: "", usdc: "", btc: "" }); }
            }}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "linear-gradient(135deg,#0EA5E9,#38BDF8)", border: "none", borderRadius: 8, color: "#FFF", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: "0 2px 10px rgba(14,165,233,0.3)" }}
            >{<Plus size={16} strokeWidth={2} />} Add Wallet</button>
          </div>
          <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden" }}>
            {(!wallets || wallets.length === 0) && (
              <div style={{ padding: "30px 16px", textAlign: "center", color: "#94A3B8", fontSize: 14 }}>No wallets added yet.</div>
            )}
            {(wallets || []).map((w, wi) => {
              const isEditing = editingWallet === w.id;
              return (
                <div key={w.id} style={{ padding: "18px 24px", borderBottom: wi < wallets.length - 1 ? "1px solid #CBD5E1" : "none" }}>
                  {isEditing ? (
                    <div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>Date</label>
                        <input type="date" value={walletForm.date} onChange={e => setWalletForm(p => ({ ...p, date: e.target.value }))}
                          style={{ ...inp, marginTop: 4, maxWidth: 200 }} />
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>TRC</label>
                        <input value={walletForm.trc} onChange={e => setWalletForm(p => ({ ...p, trc: e.target.value }))}
                          style={{ ...inp, marginTop: 4, fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }} placeholder="TRC20 address..." />
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>ERC USDT/USDC</label>
                        <input value={walletForm.erc} onChange={e => setWalletForm(p => ({ ...p, erc: e.target.value }))}
                          style={{ ...inp, marginTop: 4, fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }} placeholder="ERC20 address..." />
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>USDC</label>
                        <input value={walletForm.usdc || ""} onChange={e => setWalletForm(p => ({ ...p, usdc: e.target.value }))}
                          style={{ ...inp, marginTop: 4, fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }} placeholder="USDC address..." />
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>BTC</label>
                        <input value={walletForm.btc} onChange={e => setWalletForm(p => ({ ...p, btc: e.target.value }))}
                          style={{ ...inp, marginTop: 4, fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }} placeholder="BTC address..." />
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={async () => {
                          // FIX: stamp updatedAt so server accepts the edit (was keeping old Feb data)
                          const updated = (wallets||[]).map(ww => ww.id === w.id ? { ...ww, ...walletForm, updatedAt: stampUpdate(ww.updatedAt) } : ww);
                          const ok = await saveWalletsDirectly(updated, `edit wallet ${w.id}`);
                          if (ok) setEditingWallet(null);
                        }}
                          style={{ padding: "8px 18px", background: "linear-gradient(135deg, #059669 0%, #10B981 50%, #34D399 100%)", border: "none", borderRadius: 8, color: "#FFF", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Save</button>
                        <button onClick={() => setEditingWallet(null)}
                          style={{ padding: "8px 18px", background: "transparent", border: "1px solid #E2E8F0", borderRadius: 8, color: "#64748B", cursor: "pointer", fontSize: 13 }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                          Wallets ({w.date ? (() => { const p = w.date.split('-'); return p.length===3 ? `${p[2]}.${p[1]}.${p[0]}` : w.date; })() : "—"}) {/* FIX: was new Date(+T00:00:00) — parsed in local TZ causing off-by-one. Now splits string directly */}
                        </span>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button title="Edit wallet" onClick={() => { setEditingWallet(w.id); setWalletForm({ date: w.date || "", trc: w.trc || "", erc: w.erc || "", usdc: w.usdc || "", btc: w.btc || "" }); }}
                            style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 6, padding: 5, cursor: "pointer", color: "#2563EB", display: "flex" }}>{<Pencil size={14} strokeWidth={2} />}</button>
                          <button title="Remove wallet" onClick={async () => {
                            if (!(await askConfirm({
                              title: 'Удалить кошелёк?',
                              message: w.date ? `Запись за ${w.date}` : undefined,
                              confirmLabel: 'Удалить',
                              danger: true,
                            }))) return;
                            const remaining = (walletsRef.current||[]).filter(ww => ww.id !== w.id);
                            await saveWalletsDirectly(remaining, `delete wallet ${w.id}`, w.id);
                          }}
                            style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: 5, cursor: "pointer", color: "#DC2626", display: "flex" }}>{<Trash2 size={14} strokeWidth={2} />}</button>
                        </div>
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <tbody>
                          <tr style={{ borderBottom: "1px solid #CBD5E1" }}>
                            <td style={{ padding: "8px 0", fontWeight: 700, color: "#64748B", width: 120 }}>TRC:</td>
                            <td style={{ padding: "8px 0", fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#0F172A", wordBreak: "break-all" }}>{w.trc || "—"}</td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #CBD5E1" }}>
                            <td style={{ padding: "8px 0", fontWeight: 700, color: "#64748B" }}>ERC USDT/USDC:</td>
                            <td style={{ padding: "8px 0", fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#0F172A", wordBreak: "break-all" }}>{w.erc || "—"}</td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #CBD5E1" }}>
                            <td style={{ padding: "8px 0", fontWeight: 700, color: "#64748B" }}>USDC:</td>
                            <td style={{ padding: "8px 0", fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#0F172A", wordBreak: "break-all" }}>{w.usdc || "—"}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: "8px 0", fontWeight: 700, color: "#64748B" }}>BTC:</td>
                            <td style={{ padding: "8px 0", fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#0F172A", wordBreak: "break-all" }}>{w.btc || "—"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </main>
      {addOpen && (
        <Modal title="Add New User" onClose={() => setAddOpen(false)}>
          <Field label="Name"><input style={inp} value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} placeholder="Display name" /></Field>
          <Field label="Email"><input style={inp} type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="user@email.com" /></Field>
          <Field label="Password"><input style={inp} value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="Password" /></Field>
          <Field label="Page Access">
            <PageAccessToggles access={newUser.pageAccess} onChange={pa => setNewUser(p => ({ ...p, pageAccess: pa }))} />
          </Field>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={() => setAddOpen(false)} style={{ padding: "10px 20px", borderRadius: 8, background: "transparent", border: "1px solid #E2E8F0", color: "#64748B", cursor: "pointer", fontSize: 14 }}>Cancel</button>
            <button onClick={handleAddUser} style={{ padding: "10px 24px", borderRadius: 8, background: "linear-gradient(135deg,#0EA5E9,#38BDF8)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 600, boxShadow: "0 4px 15px rgba(14,165,233,0.3)" }}>Add User</button>
          </div>
        </Modal>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal title="Edit User" onClose={() => setEditUser(null)}>
          <Field label="Name"><input style={inp} value={editUser.name} onChange={e => setEditUser(p => ({ ...p, name: e.target.value }))} /></Field>
          <Field label="Email"><input style={inp} type="email" autoComplete="email" value={editUser.email} onChange={e => setEditUser(p => ({ ...p, email: e.target.value }))} /></Field>
          <Field label="New Password"><input style={inp} type="password" autoComplete="new-password" value={editUser.password || ""} onChange={e => setEditUser(p => ({ ...p, password: e.target.value }))} placeholder="Leave blank to keep current" /></Field>
          <Field label="Telegram Chat ID">
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <input style={{...inp,flex:1}} type="text" inputMode="numeric"
                value={editUser.telegramChatId || ''}
                onChange={e => setEditUser(p => ({ ...p, telegramChatId: e.target.value.replace(/[^0-9]/g,'') }))}
                placeholder="e.g. 123456789" />
              {editUser.telegramChatId && <span style={{fontSize:12,color:'#34d399',whiteSpace:'nowrap'}}>✓ Set</span>}
            </div>
            <span style={{fontSize:11,color:'#64748B',display:'block',marginTop:4}}>User sends /myid to @blitz_finance_bot to get their ID. Once set, new-device logins require a Telegram code.</span>
          </Field>
          <Field label="Telegram Username 🤖">
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{color:'#94A3B8',fontSize:14,fontWeight:600}}>@</span>
              <input style={{...inp,flex:1}} type="text"
                value={(editUser.telegramUsername || '').replace(/^@/,'')}
                onChange={e => setEditUser(p => ({ ...p, telegramUsername: e.target.value.replace(/^@/,'').replace(/[^a-zA-Z0-9_]/g,'') }))}
                placeholder="username (without @)" />
              {editUser.telegramUsername && <span style={{fontSize:12,color:'#34d399',whiteSpace:'nowrap'}}>✓ Whitelisted</span>}
            </div>
            <span style={{fontSize:11,color:'#64748B',display:'block',marginTop:4}}>Bot whitelist — only users with a username set here can send commands to the bot. Set to match their Telegram @username exactly.</span>
          </Field>
          {!isAdmin(editUser.email) && (
            <Field label="Page Access">
              <PageAccessToggles access={editUser.pageAccess || ["overview","settings"]} onChange={pa => setEditUser(p => ({ ...p, pageAccess: pa }))} />
            </Field>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 8, flexWrap: 'wrap' }}>
            {/* v31.42 Fix #3: Reset Password button. Amber theme — destructive (пользователь будет logout'нут),
                но не red (не delete user). Клик → confirm → backend generates password → modal shows plain pw. */}
            <button onClick={handleResetPassword} disabled={resettingPassword}
              style={{ padding: "10px 16px", borderRadius: 8, background: resettingPassword ? "#F3F4F6" : "#FEF3C7",
                       border: "1px solid #F59E0B", color: "#92400E", cursor: resettingPassword ? "not-allowed" : "pointer",
                       fontSize: 13, fontWeight: 600 }}>
              {resettingPassword ? '⏳ Resetting...' : '🔐 Reset Password'}
            </button>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setEditUser(null)} style={{ padding: "10px 20px", borderRadius: 8, background: "transparent", border: "1px solid #E2E8F0", color: "#64748B", cursor: "pointer", fontSize: 14 }}>Cancel</button>
              <button onClick={handleUpdateUser} disabled={savingUser} style={{ padding: "10px 24px", borderRadius: 8, background: savingUser ? "#94A3B8" : "linear-gradient(135deg,#0EA5E9,#38BDF8)", border: "none", color: "#FFF", cursor: savingUser ? "not-allowed" : "pointer", fontSize: 14, fontWeight: 600, boxShadow: savingUser ? "none" : "0 4px 15px rgba(14,165,233,0.3)", transition: "all 0.2s" }}>{savingUser ? "Saving..." : "Save Changes"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* v31.42 Fix #3: Reset Password result modal — shown after backend returns plain password.
          Admin копирует и передаёт user'у в TG. После закрытия modal password больше нельзя узнать
          (только новый reset). */}
      {resetPasswordModal && (
        <Modal onClose={() => setResetPasswordModal(null)} title="🔑 Password Reset Successful">
          <div style={{ padding: 4 }}>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 12 }}>
              Новый пароль для <b>{resetPasswordModal.email}</b>:
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <input readOnly value={resetPasswordModal.password}
                style={{ flex: 1, padding: '10px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 16,
                         background: '#F8FAFC', border: '2px solid #0EA5E9', borderRadius: 8, color: '#0F172A',
                         fontWeight: 700, letterSpacing: 0.5 }} />
              <button onClick={() => { try { navigator.clipboard.writeText(resetPasswordModal.password); addToast('Password copied', 'success'); } catch { addToast('Copy failed — select manually', 'error'); } }}
                style={{ padding: '10px 16px', borderRadius: 8, background: '#0EA5E9', border: 'none',
                         color: '#FFF', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>📋 Copy</button>
            </div>
            <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: 12, fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
              ⚠️ <b>Показать пароль только один раз.</b> Передай в TG лично пользователю.
              После login попроси сменить через <b>Settings → Change Password</b>.
              Все активные сессии {resetPasswordModal.email} завершены.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setResetPasswordModal(null)}
                style={{ padding: '10px 20px', borderRadius: 8, background: '#0F172A', border: 'none',
                         color: '#FFF', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Закрыть</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Restore Database (Admin only) ── */}
      <RestoreDatabase user={user} />

      {/* Server Diagnostics — Admin only */}
      <ServerDiagnostics />

      {/* Подтверждения действий панели (сброс пароля, удаление пользователя/кошелька) */}
      {confirmState && (
        <ConfirmDialog
          {...confirmState.opts}
          onConfirm={() => closeConfirm(true)}
          onCancel={() => closeConfirm(false)}
        />
      )}
    </div>
  );
}

export { AdminPanel };
export default AdminPanel;
